import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { defaultOtpChannel } from 'constants/otp';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { type OtpPurpose } from 'hooks/useOtpChallenge';
import { Button, GoBackIcon } from 'ui';
import { persistReturnTo } from 'utils/returnTo';
import { completeOtpAuthSession, enterCabinetAfterRegistration } from 'utils/completeOtpAuthSession';

import { OtpInlineBlock } from './OtpInlineBlock';
import classes from './OtpChallengePage.module.scss';

function fallbackChannel() {
  return defaultOtpChannel();
}

export const OtpChallengePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: session, isLoading: sessionLoading } = useAuthSession();
  const purpose = (searchParams.get('purpose') || 'phone_verify') as OtpPurpose;
  const phone = searchParams.get('phone') || session?.phone || undefined;
  const guest =
    purpose === 'register' || purpose === 'password_reset' || purpose === 'login';
  const signed = isAuthSessionActive(session);
  const needsLogin = !guest && !sessionLoading && !signed;
  const [challengeId, setChallengeId] = useState(searchParams.get('challenge_id') || '');
  const [channel, setChannel] = useState(searchParams.get('channel') || fallbackChannel());

  useEffect(() => {
    setChallengeId(searchParams.get('challenge_id') || '');
    setChannel(searchParams.get('channel') || fallbackChannel());
  }, [searchParams]);

  const handleBack = () => {
    if (purpose === 'register' || purpose === 'login') {
      navigate(purpose === 'login' ? '/login' : '/registration');
      return;
    }
    if (purpose === 'password_reset') {
      navigate('/forgot-password');
      return;
    }
    if (isAuthSessionActive(session) && session?.roles.includes(1)) {
      navigate('/teacher');
      return;
    }
    if (isAuthSessionActive(session) && session?.roles.includes(2)) {
      navigate('/profile');
      return;
    }
    navigate('/');
  };

  const otpReturnPath = `/otp?${searchParams.toString()}`;

  const goLogin = () => {
    persistReturnTo(otpReturnPath);
    navigate(`/login?returnTo=${encodeURIComponent(otpReturnPath)}`);
  };

  return (
    <section className={classes.wrapper}>
      <div className={classes.card}>
        <Button
          type='button'
          variant='clear'
          color='primary'
          size='clear'
          className={classes.back}
          startAdornment={<GoBackIcon />}
          onClick={handleBack}
        >
          Назад
        </Button>
        <h1 className={classes.title}>Подтверждение номера</h1>
        {needsLogin ? (
          <>
            <p className={classes.text}>
              Сессия истекла. Войдите в аккаунт, чтобы запросить и подтвердить код.
            </p>
            <div className={classes.actions}>
              <Button variant='filled' shape='brick' onClick={goLogin}>
                Войти
              </Button>
            </div>
          </>
        ) : (
          <OtpInlineBlock
            guest={guest}
            purpose={purpose}
            phone={phone}
            challengeId={challengeId}
            channel={channel}
            onAuthRequired={goLogin}
            onVerified={(data) => {
              if (data.reset_token) {
                navigate(`/reset-password?token=${encodeURIComponent(data.reset_token)}`, {
                  replace: true,
                });
                return;
              }
              if (purpose === 'register' && enterCabinetAfterRegistration(data, navigate)) {
                return;
              }
              if (completeOtpAuthSession(data, navigate)) {
                return;
              }
              if (purpose === 'login') {
                navigate('/login', { replace: true });
                return;
              }
              handleBack();
            }}
          />
        )}
      </div>
    </section>
  );
};
